import React, { useCallback } from 'react';
import { Image, Keyboard, StyleSheet, TouchableOpacity } from 'react-native';
import { GestureDetector } from 'react-native-gesture-handler';
import Animated from 'react-native-reanimated';

import InfoIcon from '@/assets/icons/info';
import { Text } from '@/components/TextComponent';
import { Box, Column } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { designSystem } from '@/styles/styles';
import { triggerHapticFeedback } from '@/utils/haptic-feedback';

import { useCasinoGameInfo } from '../../hooks/use-casino-game-info';
import { CasinoGame } from '../../hooks/use-casino-games';
import { useStartCasinoGameUrl } from '../../hooks/use-formatted-casino-game-url';
import { useTapShrinkAnimation } from '../../hooks/use-tap-shrink-animation';
import { CasinoLayoutProps } from '../../utils/utils';
import { useCasinoGameInfoModal } from '../CasinoGameInfoModalProvider';
import { LayoutHeader } from './LayoutHeader';

interface GameItemProps {
    game: CasinoGame;
}

const GameItem = ({ game }: GameItemProps) => {
    const { handleStartGame } = useStartCasinoGameUrl(game);
    const onPress = useCallback(() => {
        triggerHapticFeedback();
        handleStartGame();
    }, [handleStartGame]);
    const { gameInfoText } = useCasinoGameInfo(game);
    const { tapGesture, animatedStyle } = useTapShrinkAnimation({ onPress });
    const { showGameInfo } = useCasinoGameInfoModal();

    const handleInfoPress = () => {
        Keyboard.dismiss();
        showGameInfo(game);
    };

    return (
        <Box position="relative" mb="s12">
            <GestureDetector gesture={tapGesture}>
                <Animated.View style={animatedStyle}>
                    <Box
                        flexDirection="row"
                        alignItems="center"
                        gap="s12"
                        padding="s12"
                        borderRadius="r20"
                        backgroundColor={ds.colorToken.background.neutral.subtler}
                        borderWidth={1}
                        style={styles.gameItemContainer}
                    >
                        <Box
                            width={48}
                            height={48}
                            borderRadius={'r12'}
                            overflow={'hidden'}
                            style={styles.imageContainer}
                        >
                            <Image
                                source={{ uri: `https:${game.thumbnail || game.logo}` }}
                                style={styles.image}
                                resizeMode="cover"
                            />
                        </Box>
                        <Box flex={1} pr={'s24'}>
                            <Text variant="bodyMedium2" color={ds.colorToken.text.primary} numberOfLines={2}>
                                {game.name}
                            </Text>
                            {gameInfoText ? (
                                <Text variant="bodySmall2" color={ds.colorToken.text.secondary} mt="s4">
                                    {gameInfoText}
                                </Text>
                            ) : null}
                        </Box>
                    </Box>
                </Animated.View>
            </GestureDetector>
            <TouchableOpacity onPress={handleInfoPress} hitSlop={8} style={styles.infoIconTouchable}>
                <Box padding="s2" borderRadius="r8">
                    <InfoIcon width={16} height={16} color={designSystem.colors.neutral300} />
                </Box>
            </TouchableOpacity>
        </Box>
    );
};

export const VerticalListLayout = ({ data, layoutHeaderProps }: CasinoLayoutProps) => {
    if (!data || data.length === 0) {
        return null;
    }

    return (
        <Column>
            {layoutHeaderProps ? <LayoutHeader {...layoutHeaderProps} /> : null}
            {data.map(game => (
                <GameItem key={game.id} game={game} />
            ))}
        </Column>
    );
};

const styles = StyleSheet.create({
    gameItemContainer: {
        borderColor: ds.colorValue.border.subtle,
    },
    image: {
        width: '100%',
        height: '100%',
    },
    imageContainer: {
        overflow: 'hidden',
        borderRadius: 12,
        borderCurve: 'continuous',
    },
    infoIconTouchable: {
        position: 'absolute',
        top: '50%',
        right: 12,
        marginTop: -10,
        zIndex: 10,
    },
});
